"use client";
import axios from "axios";
import { ChangeEvent, useRef, useState } from "react";
import {
    Card,
    CardContent,
    CardDescription,
    CardFooter,
    CardHeader,
    CardTitle,
} from "~/components/v3/shadcn/Card";
import { env } from "~/env.mjs";
import { EventBySlugOutput } from "~/types/EventTypesInferred";
import { RouterOutputs, api } from "~/utils/api";
import { TextArea } from "../themed/TextArea";
import { Button } from "../themed/Button";
import Badge from "../icons/Badge";
import AutoComplete from "../themed/AutoComplete";
import { cn } from "~/utils/fontsConfig";
import { Unpacked } from "~/types/helpers";
import { Label } from "../shadcn/Label";

interface ConfigEventBadgesProps {
    event: EventBySlugOutput;
    className?: string;
}

type BadgeUploaderProps = {
    eventId?: string;
    eventSlug?: string;
    communitySlug?: string;
    handleAddBadge?: (badgeId?: string) => void;
    isModal?: boolean;
};

export const BadgeUploader = ({
    eventId,
    eventSlug,
    communitySlug,
    handleAddBadge,
    isModal,
}: BadgeUploaderProps) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [badgeName, setBadgeName] = useState("");
    const [badgeDescription, setBadgeDescription] = useState("");
    const [imagePreview, setImagePreview] = useState<string | null>(null); // State for image preview
    const [file, setFile] = useState<File | null>(null);
    const [selectedBadge, setSelectedBadge] = useState<Unpacked<
        RouterOutputs["badge"]["getBadgesByEventId"]
    > | null>(null);

    const apiContext = api.useUtils();

    const { data: badges } = api.badge.getBadgesByCommunitySlug.useQuery(
        {
            communitySlug: communitySlug ?? "",
        },
        {
            enabled: !!communitySlug,
        },
    );

    const { mutateAsync: fetchPresignedUrls } =
        api.s3.getStandardUploadPresignedUrl.useMutation();
    const createBadgeMutation = api.badge.createBadgeV3.useMutation();

    const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || !e.target.files[0]) return;

        const selectedFile = e.target.files[0];
        setFile(selectedFile);

        // Read the selected file and set the image preview
        const reader = new FileReader();
        reader.onload = (event) => {
            if (event.target?.result) {
                setImagePreview(event.target.result as string);
            }
        };
        reader.readAsDataURL(selectedFile);
    };

    const handleUploadToS3 = async () => {
        if (!file) return;
        const ext = file.name.substring(file.name.lastIndexOf("."));
        const uploadFilename = badgeName + ext;
        const pathName = badgeName.replace(/ /g, "_").toLowerCase();
        const folderPath = "badges/" + (communitySlug ?? "others");

        const presignedURL = await fetchPresignedUrls({
            key: `${folderPath}/${pathName}.png`,
        });
        let newFile = new File([file], uploadFilename, {
            type: file.type,
        });

        await axios
            .put(presignedURL, newFile.slice(), {
                headers: { "Content-Type": newFile.type },
            })
            .catch((err) => console.error(err));

        await createBadgeMutation.mutateAsync({
            eventId: eventId,
            eventSlug: eventSlug ?? "",
            communitySlug: communitySlug ?? "",
            path: `${env.NEXT_PUBLIC_ASSETS_URL}/${folderPath}/${pathName}.png`,
            name: badgeName,
            description: badgeDescription,
        });

        apiContext.badge.invalidate();
        reset();
    };

    const onSelectBadge = (id?: string) => {
        reset();
        if (!id) return;

        const badge = badges?.find((b) => b.id === id);
        setSelectedBadge(badge ?? null);
        setBadgeDescription(badge?.description ?? "");
        setImagePreview(badge?.path ?? null);
    };

    const reset = () => {
        setSelectedBadge(null);
        setImagePreview(null);
        setBadgeName("");
        setBadgeDescription("");
    };

    return (
        <div className="flex w-full flex-col gap-4">
            <div className="flex w-full flex-col items-start gap-6">
                <AutoComplete
                    name="badgeName"
                    className={cn("w-full", isModal ? "border-v3-N-400" : "")}
                    label="Badge Name"
                    placeholder="Badge Name"
                    query={badgeName}
                    setQuery={setBadgeName}
                    onChange={() => {
                        if (selectedBadge !== null) {
                            setImagePreview(null);
                            setBadgeDescription("");
                        }
                        setSelectedBadge(null);
                    }}
                    setSelected={(selected) => onSelectBadge(selected?.id)}
                    values={
                        badges?.map((b) => ({
                            displayValue: b.name,
                            id: b.id,
                            description: b.description,
                        })) ?? []
                    }
                />
                <div className="flex w-full flex-col gap-2">
                    <Label className="text-sm font-normal tracking-wide text-[#A3A8B4]">
                        Badge Description
                    </Label>
                    <TextArea
                        disabled={selectedBadge !== null}
                        className={cn(
                            "min-h-max w-full",
                            isModal ? "border-v3-N-400" : "",
                        )}
                        placeholder="Badge Description"
                        value={badgeDescription}
                        onChange={(e) => setBadgeDescription(e.target.value)}
                    />
                </div>
            </div>
            {imagePreview && (
                <div>
                    <span>Badge Preview:</span>
                    <img
                        src={imagePreview}
                        alt="Image Preview"
                        className="h-14 w-14"
                    />
                </div>
            )}

            <div className="flex w-full justify-end gap-4">
                {selectedBadge === null && (
                    <Button
                        onClick={() => fileInputRef.current?.click()}
                        disabled={selectedBadge !== null}
                        className="disabled:opacity-100"
                    >
                        {!imagePreview ? "Upload badge" : "Change badge"}
                    </Button>
                )}

                <input
                    ref={fileInputRef}
                    type="file"
                    onChange={handleFileChange}
                    accept="image/png, image/gif, image/jpeg, image/webp"
                    hidden
                />

                {selectedBadge !== null ? (
                    <Button
                        className="text-primary disabled:opacity-100"
                        onClick={() => {
                            handleAddBadge?.(selectedBadge.id);
                            reset();
                        }}
                    >
                        Add this badge
                    </Button>
                ) : (
                    <Button
                        disabled={!file || !badgeName}
                        className="text-primary disabled:opacity-100"
                        onClick={handleUploadToS3}
                    >
                        Add badge
                    </Button>
                )}
            </div>
        </div>
    );
};

const ConfigEventBadges = ({ event, className }: ConfigEventBadgesProps) => {
    const { data: eventbadges, refetch: refetchEventBadges } =
        api.badge.getBadgesByEventId.useQuery(event.id);

    const removeBadgeMutation = api.badge.removeBadgeFromEvent.useMutation();
    const badgeMutation = api.badge.addBadgeToEvent.useMutation();

    const handleAddBadgeToEvent = async (badgeId?: string) => {
        if (!event.id || !badgeId)
            throw new Error("Missing eventId or badgeId");
        await badgeMutation.mutateAsync({
            eventSlug: event.slug ?? "",
            communityId: event.community?.id ?? "",
            eventId: event.id,
            badgeId: badgeId,
        });
        refetchEventBadges();
    };

    return (
        <Card className={className || ""}>
            <CardHeader>
                <CardTitle>Badges</CardTitle>
                <CardDescription>Manage Badges for this event</CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
                {eventbadges && eventbadges.length > 0 && (
                    <>
                        <h6>Current event badges:</h6>
                        <div className="flex flex-wrap gap-2">
                            {eventbadges?.map((badge) => {
                                return (
                                    <div className=" flex flex-col items-center justify-center gap-1">
                                        <div className="flex rounded-md border-2 p-2">
                                            <Badge
                                                key={badge.id}
                                                badge={badge}
                                            />
                                        </div>
                                        <Button
                                            variant="destructive"
                                            className="h-min w-[95%] p-1"
                                            onClick={async () => {
                                                await removeBadgeMutation.mutateAsync(
                                                    {
                                                        eventSlug:
                                                            event.slug ?? "",
                                                        communitySlug:
                                                            event.community
                                                                ?.slug ?? "",
                                                        eventId: event.id,
                                                        badgeId: badge.id,
                                                    },
                                                );
                                                refetchEventBadges();
                                            }}
                                        >
                                            Remove
                                        </Button>
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}
            </CardContent>
            <CardFooter>
                <BadgeUploader
                    communitySlug={event.community?.slug}
                    eventId={event.id}
                    handleAddBadge={handleAddBadgeToEvent}
                />
            </CardFooter>
        </Card>
    );
};

export default ConfigEventBadges;
